export const javascriptPosts = [
  {
    id: 6,
    slug: "asynchronous-javascript-deep-dive",
    title: "Asynchronous JS Deep Dive",
    excerpt: "Master Promises, Async/Await, and the Event Loop once and for all with clear examples and visual explanations.",
    date: "April 05, 2024",
    readTime: "15 min read",
    category: "JavaScript",
    image: "https://images.unsplash.com/photo-1551033406-611cf9a28f67?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Beyond the Basics</h2>
      <p>JavaScript's asynchronous nature is what makes it unique and powerful. However, it's also the source of most developer headaches. Understanding how the language handles tasks under the hood is the key to writing clean, bug-free code.</p>
      <h3>The Event Loop Explained</h3>
      <p>Learn exactly how the stack, heap, and task queues interact. We'll demystify why <code>setTimeout(0)</code> doesn't run immediately and how Microtasks take precedence over Macrotasks, ensuring your promises resolve in the order you expect.</p>
      <h3>Async/Await Mastery</h3>
      <p>Discover the cleanest ways to handle asynchronous flows. We'll cover error handling strategies using <code>try/catch</code> and how to avoid common pitfalls like "await in a loop" which can severely slow down your application.</p>
      <h3>Parallelism and Performance</h3>
      <p>Using <code>Promise.all</code> and <code>Promise.settled</code> correctly can significantly improve the speed of your network-bound operations. We'll explore how to run tasks in parallel while still managing errors gracefully.</p>
    `,
    keywords: "JavaScript, Async, Promises, Event Loop"
  },
  {
    id: 7,
    slug: "functional-programming-in-js",
    title: "Functional Programming in JS",
    excerpt: "Learn how to write more predictable and testable code using pure functions, immutability, and higher-order functions.",
    date: "April 10, 2024",
    readTime: "12 min read",
    category: "JavaScript",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Functional Mindset</h2>
      <p>Functional programming is a paradigm that treats computation as the evaluation of mathematical functions. In JavaScript, this leads to code that is less prone to side effects and easier to reason about.</p>
      <h3>Pure Functions and Immutability</h3>
      <p>Learn why avoiding state mutation is the secret to building scalable applications. We'll explore techniques for working with objects and arrays without changing the originals, making your debug sessions much smoother.</p>
      <h3>Higher-Order Functions</h3>
      <p>Master <code>map</code>, <code>filter</code>, and <code>reduce</code>. These are the building blocks of functional JS. We'll show how to chain these functions to perform complex data transformations in a readable and concise way.</p>
      <h3>Currying and Composition</h3>
      <p>Discover advanced techniques like currying and function composition. These patterns allow you to build complex logic by combining small, reusable functions—the Lego blocks of software development.</p>
    `,
    keywords: "Functional Programming, JavaScript, Pure Functions, Immutability"
  },
  {
    id: 8,
    slug: "modern-es-features-to-use-now",
    title: "Modern ES Features to Use Now",
    excerpt: "A look at the latest ECMAScript features from ES2022 to ES2024 that will make your code cleaner and faster.",
    date: "April 15, 2024",
    readTime: "10 min read",
    category: "JavaScript",
    image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Stay Ahead of the Curve</h2>
      <p>JavaScript is evolving faster than ever. Every year, new features make the language more expressive and powerful. Staying up to date ensures you're writing the most efficient code possible.</p>
      <h3>Optional Chaining and Nullish Coalescing</h3>
      <p>Say goodbye to long chains of <code>if (object && object.child && object.child.name)</code>. Learn how <code>?.</code> and <code>??</code> can simplify your data access and default value handling significantly.</p>
      <h3>Array.at() and New Array Methods</h3>
      <p>Discover the convenience of <code>Array.at(-1)</code> for getting the last element and new methods like <code>toSorted</code> and <code>toSpliced</code> that help you maintain immutability without the extra <code>[...arr]</code> boilerplate.</p>
      <h3>Top-level Await and Private Fields</h3>
      <p>We'll dive into how top-level await simplifies module loading and how private class fields (<code>#property</code>) provide true encapsulation in your object-oriented code.</p>
    `,
    keywords: "ES2023, ES2024, JavaScript Features, Modern JS"
  },
  {
    id: 9,
    slug: "javascript-engines-how-v8-works",
    title: "How JavaScript Engines Work",
    excerpt: "Understand the JIT compiler, garbage collection, and optimization strategies of the V8 engine.",
    date: "April 20, 2024",
    readTime: "14 min read",
    category: "JavaScript",
    image: "https://images.unsplash.com/photo-1550439062-609e1531270e?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Under the Hood of V8</h2>
      <p>V8 is the engine that powers Chrome and Node.js. Knowing how it interprets and optimizes your code will make you a better developer, allowing you to write code that the engine loves.</p>
      <h3>JIT Compilation</h3>
      <p>Learn how V8 uses Just-In-Time (JIT) compilation to turn your script into highly optimized machine code. We'll explore the "Ignition" interpreter and "TurboFan" optimizer and how they work together to make JS blazing fast.</p>
      <h3>The Garbage Collector</h3>
      <p>Memory management in JS is automatic, but not magical. We'll dive into the Orinoco garbage collector, understanding the Generational Hypothesis and how to avoid memory leaks by managing your references correctly.</p>
      <h3>Optimizing for V8</h3>
      <p>Discover patterns that trigger optimization (and those that deoptimize your code). We'll look at "hidden classes" and "inline caches" and why consistent object shapes are vital for peak performance.</p>
    `,
    keywords: "V8 Engine, JavaScript Internals, Performance, Garbage Collection"
  },
  {
    id: 10,
    slug: "mastering-js-proxies-and-reflect",
    title: "Mastering JS Proxies & Reflect",
    excerpt: "Learn how to use Proxies for observation, validation, and creating sophisticated developer tools.",
    date: "April 25, 2024",
    readTime: "9 min read",
    category: "JavaScript",
    image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Meta-Programming in JS</h2>
      <p>Proxies allow you to intercept and customize low-level object operations. It's the ultimate tool for advanced state management, validation libraries, and creating developer-friendly APIs.</p>
      <h3>The Trap Architecture</h3>
      <p>Learn about the various "traps" like <code>get</code>, <code>set</code>, and <code>apply</code>. We'll show you how to build a reactive object system that automatically updates the UI when properties change, similar to Vue or MobX.</p>
      <h3>Reflect API</h3>
      <p>Understand why <code>Reflect</code> is the perfect companion to <code>Proxy</code>. It provides the default behavior for all traps, ensuring your proxies remain elegant and predictable without reinventing the base JS logic.</p>
      <h3>Practical Use Cases</h3>
      <p>From type-checking at runtime to building self-mocking objects for testing, we'll explore real-world scenarios where Proxies solve complex problems with minimal code.</p>
    `,
    keywords: "JavaScript Proxies, Reflect API, Meta-programming, State Management"
  }
];
